import { useEffect, useState, type FormEvent } from "react";
import { integrationsApi } from "../api.js";
import { Button, Card, EmptyState, ErrorBanner, Field, PageHeader, Select, Spinner, TextInput } from "../components/ui.js";

interface ConnectorDto { code: string; name: string; scope: string; requiresCredential: boolean; }
interface RunDto { id: string; connectorCode: string; status: string; recordsAccepted: number; recordsRejected: number; }

export function formatConnectorScope(scope: string): string {
  return scope
    .split(".")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function IntegrationsPage() {
  const [connectors, setConnectors] = useState<ConnectorDto[] | null>(null);
  const [runs, setRuns] = useState<RunDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = async () => {
    const r = await integrationsApi.runs();
    setRuns(r.data as RunDto[] ?? []);
  };
  useEffect(() => {
    void (async () => {
      const c = await integrationsApi.connectors();
      if (!c.ok) setError(c.error?.errorCode ?? "Connectors unavailable");
      else setConnectors(c.data as ConnectorDto[] ?? []);
      void loadRuns();
    })();
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!connectors) return <Spinner />;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Connected data"
        title="Integrations"
        description="Review supported connectors and import data through Nettstay's governed integration boundary."
      />
      <Card title="Available connectors" description="The data sources currently supported by your account.">
        {connectors.length === 0 ? <EmptyState title="No connectors available" message="There are no supported connectors for this account yet." /> : (
          <ul className="connector-list">{connectors.map((connector) => (
            <li key={connector.code}>
              <strong>{connector.name}</strong>
              <span className="connector-scope">{formatConnectorScope(connector.scope)}</span>
            </li>
          ))}</ul>
        )}
      </Card>
      <Card title="Import data" description="Choose a supported connector and provide data in its accepted format.">
        <ImportForm connectors={connectors} onDone={loadRuns} />
      </Card>
      <Card title="Import history" description="The latest import outcomes for your account.">
        {runs === null ? <Spinner /> : runs.length === 0 ? <EmptyState title="No imports yet" message="Completed imports will appear here with accepted and rejected record counts." /> : (
          <div className="table-shell"><table className="data-table"><thead><tr><th>Connector</th><th>Status</th><th>Accepted</th><th>Rejected</th></tr></thead>
            <tbody>{runs.map((run) => <tr key={run.id}><td>{run.connectorCode}</td><td>{run.status}</td><td>{run.recordsAccepted}</td><td>{run.recordsRejected}</td></tr>)}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

function ImportForm({ connectors, onDone }: { connectors: ConnectorDto[]; onDone: () => void }) {
  const [form, setForm] = useState({ connectorCode: "", externalSourceId: "manual-upload", payload: "date,property,amount,currency,type,category,description\n2025-01-15,My Property,1200.00,NZD,income,rent,Monthly rent" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setBusy(true); setResult(null);
    const res = await integrationsApi.import({ connectorCode: form.connectorCode, externalSourceId: form.externalSourceId, payload: form.payload });
    setBusy(false);
    if (!res.ok) { setError(res.error?.errorCode ?? "Import failed"); return; }
    const data = res.data as { id?: string; recordsAccepted?: number; recordsRejected?: number; status?: string };
    setResult(`Import ${data.status ?? "started"} — run ${data.id ?? "?"} (${data.recordsAccepted ?? 0} accepted, ${data.recordsRejected ?? 0} rejected)`);
    onDone();
  };

  return (
    <form onSubmit={onSubmit} className="form-stack">
      {error && <ErrorBanner message={error} />}
      {result && <p className="success-text">{result}</p>}
      <Field label="Connector">
        <Select required value={form.connectorCode} onChange={(e) => setForm({ ...form, connectorCode: e.target.value })}>
          <option value="">Select connector…</option>
          {connectors.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </Select>
      </Field>
      <Field label="Source ID"><TextInput required value={form.externalSourceId} onChange={(e) => setForm({ ...form, externalSourceId: e.target.value })} /></Field>
      <Field label="Payload (CSV)"><textarea className="input" rows={5} required value={form.payload} onChange={(e) => setForm({ ...form, payload: e.target.value })} /></Field>
      <Button type="submit" disabled={busy}>{busy ? "Importing…" : "Start import"}</Button>
    </form>
  );
}
